import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from './components/ui/sonner';

// Import contexts
import { UserProvider } from './contexts/UserContext';
import { FeeProvider } from './context/FeeContext';
import { WebSocketProvider } from './contexts/WebSocketContext';

// Import pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ResetPassword from './pages/ResetPassword';
import RegisterPage from './pages/RegisterPage';
import Dashboard from './pages/Dashboard';
import BrowseServices from './pages/BrowseServices';
import BrowseJobs from './pages/BrowseJobs';
import ServiceDetail from './pages/ServiceDetail';
import CreateService from './pages/CreateService';
import CreateJob from './pages/CreateJob';
import Notifications from './pages/Notifications';
import Inquiries from './pages/Inquiries';
import Profile from './pages/Profile';
import ProviderProfile from './pages/ProviderProfile';
import Favorites from './pages/Favorites';
import Earnings from './pages/Earnings';
import Payments from './pages/Payments';
import AdminDashboard from './pages/AdminDashboard';
import AdminDashboardFull from './pages/AdminDashboardFull';
import AdminIDVerification from './pages/AdminIDVerification';
import FinancialAnalytics from './pages/FinancialAnalytics';
import OwnerPanel from './pages/OwnerPanel';
import Settings from './pages/Settings';
import PaymentMethodsPage from './pages/PaymentMethodsPage';
import AccountSettings from './pages/AccountSettings';
import PostService from './pages/PostService';
import EditJob from './pages/EditJob';
import EditBooking from './pages/EditBooking';
import MyServices from './pages/MyServices';
import MyBookings from './pages/MyBookings';
import PaymentCheckout from './pages/PaymentCheckout';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import HelpPage from './pages/HelpPage';
import MyBids from './pages/MyBids';
import DisputeDetail from './pages/DisputeDetail';
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SuccessStoriesPage from './pages/SuccessStoriesPage';
import ResourcesPage from './pages/ResourcesPage';
import SupportChat from './components/SupportChat';
import AnnouncementPopup from './components/AnnouncementPopup';
import RealtimeToastListener from './components/RealtimeToastListener';
import { MobileNavProvider } from './contexts/MobileNavContext';
import { useIsNativeApp } from './utils/platform';
import MobileBottomTabBar from './components/mobile/MobileBottomTabBar';
import MobileMenuDrawer from './components/mobile/MobileMenuDrawer';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';
import { setAppLanguage } from './i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios instance with auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isNativeApp = useIsNativeApp();

  useEffect(() => {
    checkAuth();
  }, []);

  // Initialize native push notifications once a user is logged in (no-op on web).
  useEffect(() => {
    if (user) {
      import('./utils/pushNotifications')
        .then(({ initPushNotifications }) => initPushNotifications())
        .catch(() => {});
    }
  }, [user]);

  // Apply the user's saved language preference on login / app load (any device).
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/settings`)
      .then((res) => {
        const lang = res.data?.language;
        if (lang) setAppLanguage(lang === 'fil' ? 'fil' : 'en');
      })
      .catch(() => {});
  }, [user]);

  // Also subscribe to localStorage changes (for role switches)
  useEffect(() => {
    const handleStorageChange = () => {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (e) {
          console.error('Error parsing user from storage:', e);
        }
      }
    };

    // Listen for custom event when user is updated
    window.addEventListener('userUpdated', handleStorageChange);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('userUpdated', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Mobile drawer logout -> clear App user state too
  useEffect(() => {
    const handleLogoutEvent = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    };
    window.addEventListener('gj:logout', handleLogoutEvent);
    return () => window.removeEventListener('gj:logout', handleLogoutEvent);
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    // Notify UserContext (mobile drawer / tab bar) to sync immediately.
    window.dispatchEvent(new Event('userUpdated'));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.dispatchEvent(new Event('userUpdated'));
  };

  // Function to update user state (used by child components)
  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    // Dispatch custom event for other components
    window.dispatchEvent(new Event('userUpdated'));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl font-semibold text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <FeeProvider>
    <UserProvider>
    <WebSocketProvider>
    <MobileNavProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<LandingPage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />} />
        <Route 
          path="/login" 
          element={user ? <Navigate to="/dashboard" /> : <LoginPage onLogin={handleLogin} />} 
        />
        <Route 
          path="/register" 
          element={user ? <Navigate to="/dashboard" /> : <RegisterPage onLogin={handleLogin} />} 
        />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route 
          path="/dashboard" 
          element={user ? <Dashboard user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/services" 
          element={<BrowseServices user={user} onLogout={handleLogout} />} 
        />
        <Route 
          path="/jobs" 
          element={<BrowseJobs user={user} onLogout={handleLogout} />} 
        />
        <Route 
          path="/help" 
          element={<HelpPage user={user} onLogout={handleLogout} />} 
        />
        <Route 
          path="/my-bids" 
          element={user ? <MyBids user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/services/:id" 
          element={<ServiceDetail user={user} onLogout={handleLogout} />} 
        />
        <Route 
          path="/create-service" 
          element={user && user.role === 'provider' ? <CreateService user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
        />
        <Route 
          path="/post-service" 
          element={user && (user.role === 'provider' || user.active_role === 'provider') ? <PostService user={user} onLogout={handleLogout} onUpdate={handleUserUpdate} /> : <Navigate to="/" />} 
        />
        <Route 
          path="/create-job" 
          element={user ? <CreateJob user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
        />
        <Route 
          path="/edit-job/:id" 
          element={user ? <EditJob user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/edit-booking/:id" 
          element={user ? <EditBooking user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/my-services" 
          element={user ? <MyServices user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/my-bookings" 
          element={user ? <MyBookings user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/payment/:bookingId" 
          element={user ? <PaymentCheckout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/payment/success" 
          element={user ? <PaymentSuccess user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/payment/failed" 
          element={user ? <PaymentFailed user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/notifications" 
          element={user ? <Notifications user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/inquiries" 
          element={user ? <Inquiries user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/inquiries/:inquiryId" 
          element={user ? <Inquiries user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/profile" 
          element={user ? <Profile user={user} onLogout={handleLogout} onUpdate={handleUserUpdate} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/provider/:id" 
          element={<ProviderProfile user={user} onLogout={handleLogout} />} 
        />
        <Route 
          path="/user/:id" 
          element={<ProviderProfile user={user} onLogout={handleLogout} />} 
        />
        <Route 
          path="/favorites" 
          element={user ? <Favorites user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/earnings" 
          element={user && (user.active_role || user.role) === 'provider' ? <Earnings user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
        />
        <Route 
          path="/payments" 
          element={user && (user.active_role || user.role) === 'client' ? <Payments user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
        />
        <Route 
          path="/admin" 
          element={user ? <AdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/admin/full" 
          element={user ? <AdminDashboardFull user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/admin/id-verification" 
          element={user ? <AdminIDVerification user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/admin/analytics" 
          element={user ? <FinancialAnalytics user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/owner" 
          element={user ? <OwnerPanel user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/payment-methods" 
          element={user ? <PaymentMethodsPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/settings" 
          element={user ? <Settings user={user} onLogout={handleLogout} onUpdate={handleUserUpdate} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/account-settings" 
          element={user ? <AccountSettings user={user} onLogout={handleLogout} onUpdate={handleUserUpdate} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/disputes/:disputeId" 
          element={user ? <DisputeDetail user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/about" 
          element={<AboutPage user={user} onLogout={handleLogout} />} 
        />
        <Route 
          path="/privacy-policy" 
          element={<PrivacyPolicyPage user={user} onLogout={handleLogout} />} 
        />
        <Route 
          path="/terms-of-service" 
          element={<TermsOfServicePage user={user} onLogout={handleLogout} />} 
        />
        <Route 
          path="/success-stories" 
          element={<SuccessStoriesPage user={user} onLogout={handleLogout} />} 
        />
        <Route 
          path="/resources" 
          element={<ResourcesPage user={user} onLogout={handleLogout} />} 
        />
      </Routes>
      {/* Support chat: floating FAB is hidden on native (Help & Support opens from the
          Help page / menu drawer), but the panel is always mounted so it can be opened
          via the 'open-support-chat' event on both web and the native app. */}
      <SupportChat user={user} />

      {/* Owner announcement pop-up (shown on app open for the targeted audience) */}
      {user && <AnnouncementPopup user={user} />}
      <RealtimeToastListener />
      {isNativeApp && <MobileBottomTabBar />}
      {isNativeApp && <MobileMenuDrawer onLogout={handleLogout} />}
      {isNativeApp && <UpdateAvailableBanner />}
    </BrowserRouter>
    </MobileNavProvider>
    </WebSocketProvider>
    </UserProvider>
    </FeeProvider>
  );
}

export default App;